/**
 * @module app.ext.cwqi
 */

import { autoinject } from 'aurelia-framework';
import { format, parseISO, differenceInCalendarDays, addDays } from 'date-fns'
import { EventAggregator, Subscription } from 'aurelia-event-aggregator';
import { EnvironmentService } from 'app/shared/services/environment-service';
import { version } from 'version';
import { ApiPostResponse, ApiAction, Api, apiCall } from 'app/shared/lib/essette-api';
import { IApiSettings } from 'app/shared/lib/api/api-types';
import { getApiSetting } from 'app/shared/lib/api/api-settings';
import { ISessionUser } from 'app/shared/lib/session/session-types';
import { AuthService } from 'app/um/services/auth-service';
import { AuthExternalSystemChangeEvent } from 'app/um/lib/events';
import { SessionChangedEvent } from 'app/shared/lib/session/session-events';
import { AppLogger } from 'app/shared/lib/logging/app-logger';
import { DateTimeValue, DateValue } from 'app/shared/lib/date-time';
import { resolveProperty } from 'app/shared/lib/objects';
import { isNullOrUndefined, getAbsoluteUrl } from 'app/shared/lib/utilities';
import * as UM from 'app/um/models/essette-api';
import * as CD from 'app/cd/models/essette-api';

@autoinject()
export class CwqiManager {

    ////****************** Fields and Properties ************************************////
    private cwqiUrl: string = '';
    private instancePrefix: string = this.env.config.instancePrefix;
    private episodeIDPrefix: string | null = null;
    private patientIDProperty: string | null = null;
    private returnUrl: string | null = null;
    private auth: UM.Auth | null = null;
    private user: ISessionUser | null = null;
    private settings: IApiSettings = {};
    private sessionEvent: Subscription | null = null;

    private listener: (event: MessageEvent) => {};

    private mcgIdValue: string = 'cc7cfab2-cd19-4b6b-9656-9b8e408331f9|Gainwell_Essette';
    private noGroupNumber: string = 'NO_GROUP_ID';


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        protected readonly api: Api,
        private readonly env: EnvironmentService,
        private readonly logger: AppLogger,
        private readonly authService: AuthService,
        private readonly events: EventAggregator) {

        this.user = env.user;
        this.logger.source = this;

        this.loadSettings();

        // Need to assign listener function to a class variable, so that it is only created and assigned once, and therefore can be added and removed as a window listener function
        this.listener = this.handleCwqiMessage.bind(this);

        this.sessionEvent = this.events.subscribe(SessionChangedEvent,
            (e: SessionChangedEvent) => {
                if(['checkIn', 'logIn'].includes(e.key)) {
                    // if user context has changed (tenant, context) due to logIn/logOut/checkIn/etc.
                    this.loadSettings();
                }
            });
    }


    ////****************** Events ***************************************************////


    ////****************** Functions ************************************************////
    protected async loadSettings(): Promise<void> {
        // If a `checkIn` is necessary the `login` event will not have a tenant, and that is meaningless here.
        if(this.env.tenant.id) {
            this.settings = await this.loadSystemSettings();
            this.cwqiUrl = <string>getApiSetting(this.settings, 'cwqi.api.$url');
            this.episodeIDPrefix = <string>getApiSetting(this.settings, 'cwqi.episodeID.$prefix');
            this.patientIDProperty = <string>getApiSetting(this.settings, 'cwqi.patientID.$property');
            this.returnUrl = getAbsoluteUrl(this.env.resolveUrl('~/app/ext/cwqi/out.html'));
        }
    }

    // Open Window for normal CareWebQI Guidelines
    public open(settings: IApiSettings, auth: UM.Auth, authDetail?: UM.AuthDiagnosis | UM.AuthService): void {
        let searchTerms: string = 'quicksearch';
        if(authDetail) searchTerms = `${authDetail.definition?.code}|${authDetail.definition?.type?.code}`;

        this.auth = auth;

        const w = window.open('', 'cwqiPopup', 'top=25, left=' + (screen.width / 4) + ',width=1000,height=' + (screen.height - 150));

        if(w) {
            const f: HTMLFormElement = this.buildForm(w, searchTerms, <string>getApiSetting(settings, 'integrations.mcg.cwqi.$values'));
            this.logger.debug('[CareWebQI] Request:' + f.innerHTML);
            const promise: Promise<ApiPostResponse> = this.getHashToken(f.innerHTML);
            promise.then((response) => {
                w.document.write('<!DOCTYPE html><html><body>Opening CareWebQI Please Wait...</body></html>');

                f.appendChild(this.createInput(w, 'messageHash', response.resultText!));

                w.document.getElementsByTagName('body')[0].appendChild(f);
                f.submit();

                window.addEventListener('message', this.listener, false);
            });
        }
    }

    // Open Window for CareWebQI Auto Auth
    public openAutoAuth(auth: UM.Auth, codes: ICodes[]): void {
        this.auth = auth;
        const w = window.open('', 'cwqiAutoAuthPopup', 'top=25, left=' + (screen.width / 4) + ',width=1000,height=' + (screen.height - 150));

        this.returnUrl = getAbsoluteUrl(this.env.resolveUrl('~/app/ext/cwqi/aa-out.html'));

        if(w) {
            const f: HTMLFormElement = this.buildAutoAuthForm(w, codes);
            this.logger.debug('[CareWebQI AutoAuth] Request:' + f.innerHTML);
            const promise: Promise<ApiPostResponse> = this.getAutoAuthHashToken(f.innerHTML);
            promise.then((response) => {
                w.document.write('<!DOCTYPE html><html><body>Opening CareWebQI Auto Auth Please Wait...</body></html>');

                f.appendChild(this.createInput(w, 'messageHash', response.resultText!));

                w.document.getElementsByTagName('body')[0].appendChild(f);
                f.submit();

                window.addEventListener('message', this.listener, false);
            });
        }
    }

    private async handleCwqiMessage(event: MessageEvent): Promise<void> {
        //TODO: Check Origin of event for security.  Similar to how we check the origins for Transistional.  Could we make the Transistional origin concept a more generic XSS origin model to be used for Transistional or otherwise?
        if(typeof event.data === 'object') {
            if('episodeID' in event.data && 'authID' in event.data) {
                // Must remove the event listener, or else subsequent CWQI windows add additional listeners, results in the handler firing as many times as you've opened the window
                window.removeEventListener('message', this.listener, false);

                const msgType: string = event.data?.type || '';
                switch(msgType.toUpperCase()) {
                    case 'CWQI': {
                        this.updateExternalSystemID();
                        this.events.publish(new AuthExternalSystemChangeEvent());
                        this.events.publish(new CwqiChangedEvent());
                        break;
                    }
                    case 'CWQI-AA': {
                        this.events.publish(new AutoAuthClosedEvent());
                        this.verifyExternalSystemResult(event.data.episodeID);
                        break;
                    }
                    default:
                        break;
                }
            }
            console.log(event.data);
        } else {
            console.log('[CareWebQI] ' + event.data);
        }
    }

    // Open Window for CareWebQI Episode Summary
    public openSummary(auth: UM.Auth): void {
        this.auth = auth;
        const w = window.open('', 'cwqiSummaryPopup', 'top=25, left=' + (screen.width / 4) + ',width=1000,height=' + (screen.height - 150));

        if(w) {
            const f: HTMLFormElement = window.document.createElement('form');
            f.method = 'post';
            f.action = this.cwqiUrl;

            f.appendChild(this.createInput(window, 'documentingUser', this.user!.loginName!));
            f.appendChild(this.createInput(window, 'requestType', 'EpisodeSummary'));
            f.appendChild(this.createInput(window, 'episodeID', this.instancePrefix + this.episodeIDPrefix + this.auth.number!));
            f.appendChild(this.createInput(window, 'resultTransform', 'EpisodeSummaryHtml.xslt'));

            const promise: Promise<ApiPostResponse> = this.getHashToken(f.innerHTML);
            promise.then((response) => {
                w.document.write('<!DOCTYPE html><html><body>Opening CareWebQI Please Wait...</body></html>');

                f.appendChild(this.createInput(w, 'messageHash', response.resultText!));

                w.document.getElementsByTagName('body')[0].appendChild(f);
                f.submit();
            });
        }
    }

    public async getSummary(auth: UM.Auth, getHtml: boolean): Promise<ApiPostResponse> {
        if(!auth.number) { throw new Error('Unable to get Auth Number'); }
        if(!this.user) { throw new Error('Unable to determine current user'); }

        const f: HTMLFormElement = window.document.createElement('form');
        f.appendChild(this.createInput(window, 'documentingUser', this.user.loginName));
        f.appendChild(this.createInput(window, 'requestType', 'EpisodeSummary'));
        f.appendChild(this.createInput(window, 'episodeID', this.instancePrefix + this.episodeIDPrefix + auth.number));
        if(getHtml) { f.appendChild(this.createInput(window, 'resultTransform', 'EpisodeSummaryHtml.xslt')); }

        return this.getSummaryXml(f.innerHTML);

    }

    // Build form for CareWebQI Request
    private buildForm(w: Window, searchTerms: string, publicationCodes: string): HTMLFormElement {
        const f: HTMLFormElement = w.document.createElement('form');
        f.method = 'post';
        f.action = this.cwqiUrl;

        f.appendChild(this.createInput(w, 'requestType', 'documentation'));
        f.appendChild(this.createInput(w, 'returnUrl', this.returnUrl + `?AuthID=${this.auth!.id}`));

        this.addStandardFields(w, f);

        f.appendChild(this.createInput(w, 'guidelinePublicationCodes', publicationCodes));
        f.appendChild(this.createInput(w, 'guidelineSearchTerms', searchTerms));

        return f;
    }

    // Build form for Auto Auth Request
    private buildAutoAuthForm(w: Window, codes: ICodes[]): HTMLFormElement {
        const f: HTMLFormElement = w.document.createElement('form');
        f.method = 'post';
        f.action = this.cwqiUrl;

        f.appendChild(this.createInput(w, 'requestType', 'AuthRequest'));
        f.appendChild(this.createInput(w, 'returnUrl', this.returnUrl + `?AuthID=${this.auth!.id}`));
        f.appendChild(this.createInput(w, 'entryPoint', 'SubmitRequestStep'));

        this.addStandardFields(w, f, true);

        //Build Code List
        //Format:   "CD.Service" = {code},{type},{isPrimary},{isPreCert},{RequestedCodeUnits},{CodeDescription}
        //          "CD.Diagnosis" = {code},{type},{isPrimary},{isPreCert}
        // The logic for isPreCert is
        // isPreCert should be set to true for all procedures when documenting Services
        // isPreCert should be set to true for all diagnoses when documenting Diagnoses
        let codeList: string = '';

        let includeAuthorizationRequestedUnits: boolean = true;
        codes.forEach(code => {
            if(code.determination) { return; }

            if(code.modelCode === 'CD.Service') {
                includeAuthorizationRequestedUnits = false;
            }
            let listItem: string[] = [];
            // MCG authorizationCodeList: [code, codeType, isPrimary, isPreCert]
            listItem = [code.code, code.type, code.isPrimary ? 'true' : 'false', code.isPreCert ? 'true' : 'false'];

            if(code.modelCode === 'CD.Service') {
                // MCG RequestedCodeUnits
                listItem.push(!isNullOrUndefined(code.qty) ? code.qty.toString() : '');
                // MCG CodeDescription limit is 800 characters will be truncated with a note to refer to CPT manual.
                listItem.push(!isNullOrUndefined(code.description) ? code.description.toString().replace(/,/g, "\\,") : '');
            }

            codeList = codeList + (codeList.length > 0 ? '|' : '') + listItem.join(",");
        });
        f.appendChild(this.createInput(w, 'authorizationCodeList', codeList));

        let lengthofStay: number = 0;
        if(this.auth?.serviceDate?.dateRange?.start && this.auth?.serviceDate?.dateRange?.end) {
            let startDate: Date = new Date(this.auth?.serviceDate.dateRange.start);
            let endDate: Date = new Date(this.auth?.serviceDate.dateRange.end);
            lengthofStay = differenceInCalendarDays(addDays(endDate, 1), startDate);
        }
        if(includeAuthorizationRequestedUnits) {
            f.appendChild(this.createInput(w, 'authorizationRequestedUnits', lengthofStay.toString()));
        } else {
            f.appendChild(this.createInput(w, 'authorizationRequestedUnits', "0"));
        }
        f.appendChild(this.createInput(w, 'lengthofStay', lengthofStay.toString()));
        f.appendChild(this.createInput(w, 'authorizationUrgent', 'false'));

        let dateOfService: string = format(new Date(), 'MM/dd/yyyy');  //Default
        if(this.auth?.serviceDate?.dateRange?.start) {
            dateOfService = this.formatDate(this.auth?.serviceDate.dateRange.start, 'MM/dd/yyyy');
        }
        f.appendChild(this.createInput(w, 'RequestedDateOfService', dateOfService));  //Format: MM/dd/yyyy

        //New request types that follow the Diagnosis/Procedure {{AuthClass}} based on whether service or diagnosis is selected as documentable
        let procedureIsPreCert: boolean = codes.findIndex(c => c.isPreCert && c.modelCode === 'CD.Service') > -1;
        let authorizationRequestType: string = procedureIsPreCert ? `Procedure ${this.auth!.class!.$display!}` : `Diagnosis ${this.auth!.class!.$display!}`;
        f.appendChild(this.createInput(w, 'authorizationRequestType', authorizationRequestType));

        f.appendChild(this.createInput(w, 'authorizationRequestedLevelOfCare', this.auth!.class!.subClass!.$display!));

        //X12 Healthcare Service Type and Place of Service codes
        f.appendChild(this.createInput(w, 'serviceTypeCode', this.auth?.healthcareServiceType?.code || ''));
        f.appendChild(this.createInput(w, 'placeOfServiceCode', this.auth?.placeOfService?.code || ''));

        return f;
    }

    // Common Fields for CareWebQI and AutoAuth Requests
    private addStandardFields(w: Window, f: HTMLFormElement, isAutoAuth: boolean = false): void {
        if(!this.auth || !this.auth.number || !this.auth.member || !this.auth.provider) { throw new Error("Missing Auth information"); }
        if(!this.auth.member.birthDate) { throw new Error("Missing Member information"); }
        if(!this.auth.member.plan?.healthPlan?.$display || !this.auth.member.plan.identifier?.primary || !this.auth.member.plan?.lineOfBusiness?.$display) { throw new Error("Missing Member Plan information"); }

        f.appendChild(this.createInput(w, 'documentingUser', this.user!.loginName!));
        if(this.user!.name!.first) {
            f.appendChild(this.createInput(w, 'documentingUserFirstName', this.user!.name.first));
        }
        if(this.user!.name!.last) {
            f.appendChild(this.createInput(w, 'documentingUserLastName', this.user!.name.last));
        }
        f.appendChild(this.createInput(w, 'episodeID', this.getEpisodeID(this.auth.number)));


        //adding new fields
        f.appendChild(this.createInput(w, 'Mcg-Partner-ID', this.mcgIdValue));
        f.appendChild(this.createInput(w, 'Mcg-Solution-ID', 'Essette'));
        f.appendChild(this.createInput(w, 'Mcg-Solution-Version', this.getVersionInfo()));


        //member details
        let temp: HTMLInputElement | null = null;
        f.appendChild(temp = this.createInput(w, 'patientID', this.getPatientID(this.auth.member)));
        f.appendChild(this.createInput(w, 'patientFirstName', this.auth.member.name?.first || 'NO_FIRST_NAME'));
        f.appendChild(this.createInput(w, 'patientLastName', this.auth.member.name?.last || ''));
        f.appendChild(this.createInput(w, 'patientDateOfBirth', this.formatDate(this.auth.member.birthDate, 'MM/dd/yyyy')));
        f.appendChild(this.createInput(w, 'patientCity', this.auth.member.address?.home?.city || ''));
        f.appendChild(this.createInput(w, 'patientState', this.auth.member.address?.home?.region?.code || ''));
        f.appendChild(this.createInput(w, isAutoAuth ? 'patientZip' : 'patientZipCode', this.auth.member.address?.home?.postalCode || ''));
        if(this.auth.member.gender?.description) {
            f.appendChild(this.createInput(w, 'patientGender', this.auth.member.gender.description));
        }

        // Adding Patient Benefit Plan Information
        //get patient benefit eligibility history
        const eligibilityHistory = this.auth.member.plan?.eligibility?.history;

        type patientBenefitType = {
            planName: string,
            memberNumber: string,
            lineofBusiness: string,
            product?: string,
            groupNumber?: string,
            eligibilityDate?: string,
            disenrollmentDate?: string
        }
        // Hold Benefit information in object
        const patientBenefit: patientBenefitType = {
            planName: this.auth.member.plan.healthPlan.$display,
            memberNumber: this.auth.member.plan.identifier.primary,
            lineofBusiness: this.auth.member.plan.lineOfBusiness.$display
        };

        // Get Patient Benefit Details if Elibility History is valid
        if(eligibilityHistory && eligibilityHistory.length > 0) {
            const recentActiveHistory = eligibilityHistory.filter(h => h.isActive && h.dateRange)
                .sort((a, b) => {
                    let aDate = a.dateRange?.start ? new DateTimeValue(a.dateRange?.start) : DateTimeValue.MIN_VALUE;
                    let bDate = b.dateRange?.start ? new DateTimeValue(b.dateRange?.start) : DateTimeValue.MIN_VALUE;
                    return bDate.compareTo(aDate);
                })[0];

            if(recentActiveHistory.product?.description) {
                patientBenefit.product = recentActiveHistory.product.description;
            }

            patientBenefit.groupNumber = !isNullOrUndefined(recentActiveHistory.groupNumber) ? recentActiveHistory.groupNumber : this.noGroupNumber;

            const startDate = recentActiveHistory.dateRange?.start;
            const endDate = recentActiveHistory.dateRange?.end;

            if(startDate) {
                patientBenefit.eligibilityDate = this.formatDate(startDate, 'MM/dd/yyyy');
            }
            if(endDate) {
                patientBenefit.disenrollmentDate = this.formatDate(endDate, 'MM/dd/yyyy');
            }
        }

        // Append Benefit Information for CWQI Form
        //Required parameters
        f.appendChild(this.createInput(w, 'patientBenefitPlanName', patientBenefit.planName));
        f.appendChild(this.createInput(w, 'patientBenefitMemberID', patientBenefit.memberNumber));
        f.appendChild(this.createInput(w, 'patientLineofBusinessName', patientBenefit.lineofBusiness));

        //Optional but Recommended parameters
        if(patientBenefit.product) {
            f.appendChild(this.createInput(w, 'patientBenefitProduct', patientBenefit.product));
        }
        if(patientBenefit.groupNumber) {
            f.appendChild(this.createInput(w, 'patientBenefitGroupID', patientBenefit.groupNumber));
        }
        if(patientBenefit.eligibilityDate) {
            f.appendChild(this.createInput(w, 'patientBenefitEligibilityDate', patientBenefit.eligibilityDate));
        }
        if(patientBenefit.disenrollmentDate) {
            f.appendChild(this.createInput(w, 'patientBenefitDisenrollmentDate', patientBenefit.disenrollmentDate));
        }

        if(this.auth.isAdmission) {
            f.appendChild(this.createInput(w, 'episodeType', 'Inpatient'));
            if(this.auth.admission?.date) {
                //If admission date is somehow before member's birth date, throw an error
                if(new DateValue(this.auth.admission.date).isBefore(new DateValue(this.auth.member.birthDate))) throw new Error("Admission Date must be after Member's Birth Date");
                //Format: mm/dd/yyyy
                f.appendChild(this.createInput(w, 'episodeRequestedAdmitDate', this.formatDate(this.auth.admission.date, 'MM/dd/yyyy')));
                f.appendChild(this.createInput(w, 'episodeAdmitDate', this.formatDate(this.auth.admission.date, 'MM/dd/yyyy')));
                //Format: hh:mm:ss tt
                //We dont store time for admission date so it should always be 12:00:00 AM
                f.appendChild(this.createInput(w, 'episodeAdmitTime', this.formatDate(this.auth.admission.date, 'hh:mm:ss a')));
            } else if(this.auth.type!.code === 'PRE' && this.auth.serviceDate?.dateRange?.start) {
                f.appendChild(this.createInput(w, 'episodeRequestedAdmitDate', this.formatDate(this.auth.serviceDate?.dateRange?.start, 'MM/dd/yyyy')));
            }
            if(this.auth.discharge?.date && this.auth.discharge.location?.description) {
                //If discharge date is somehow before admission date, throw an error
                if(this.auth.admission?.date && new DateValue(this.auth.discharge.date).isBefore(new DateValue(this.auth.admission.date))) throw new Error("Discharge Date must be after Admission Date");
                //Format: mm/dd/yyyy
                f.appendChild(this.createInput(w, 'episodeDischargeDate', this.formatDate(this.auth.discharge.date, 'MM/dd/yyyy')));
                f.appendChild(this.createInput(w, 'episodeDischargeTo', this.auth.discharge.location.description));
            }
            if(this.auth.provider.attending) {
                const { identifier, name, location } = this.auth.provider.attending;
                f.appendChild(this.createInput(w, 'attendingProviderID', identifier?.primary || 'PRO-Unknown'));
                f.appendChild(this.createInput(w, 'attendingProviderFirstName', name?.first || identifier?.primary || 'NO_FIRST_NAME'));
                f.appendChild(this.createInput(w, 'attendingProviderLastName', name?.last || ''));

                const { city, region, postalCode } = location?.facility?.address?.physical || { city: null, region: null, postalCode: null };
                f.appendChild(this.createInput(w, 'attendingProviderCity', city || ''));
                f.appendChild(this.createInput(w, 'attendingProviderState', region?.code || ''));
                f.appendChild(this.createInput(w, 'attendingProviderZip', postalCode || ''));

                const npi = identifier?.nationalProvider && identifier.nationalProvider.length == 10 ? identifier.nationalProvider : '';
                f.appendChild(this.createInput(w, 'attendingProviderNPI', npi));
                if(isAutoAuth) f.appendChild(this.createInput(w, 'attendingProviderTaxID', location?.facility?.identifier?.tax || ''));

            } else {
                f.appendChild(this.createInput(w, 'attendingProviderID', 'PRO-Unknown'));
            }
        } else {
            f.appendChild(this.createInput(w, 'episodeType', 'Outpatient'));
            if(this.auth.provider.servicing) {
                const { identifier, name, location } = this.auth.provider.servicing;
                f.appendChild(this.createInput(w, 'attendingProviderID', identifier?.primary || 'PRO-Unknown'));
                f.appendChild(this.createInput(w, 'attendingProviderFirstName', name?.first || identifier?.primary || 'NO_FIRST_NAME'));
                f.appendChild(this.createInput(w, 'attendingProviderLastName', name?.last || ''));

                const { city, region, postalCode } = location?.facility?.address?.physical || { city: null, region: null, postalCode: null };
                f.appendChild(this.createInput(w, 'attendingProviderCity', city || ''));
                f.appendChild(this.createInput(w, 'attendingProviderState', region?.code || ''));
                f.appendChild(this.createInput(w, 'attendingProviderZip', postalCode || ''));

                const npi = identifier?.nationalProvider && identifier.nationalProvider.length == 10 ? identifier.nationalProvider : '';
                f.appendChild(this.createInput(w, 'attendingProviderNPI', npi));
                if(isAutoAuth) f.appendChild(this.createInput(w, 'attendingProviderTaxID', location?.facility?.identifier?.tax || ''));

            } else {
                f.appendChild(this.createInput(w, 'attendingProviderID', 'PRO-Unknown'));
            }
        }
        if(this.auth.provider.servicing?.location?.facility) {
            const { identifier, description, address } = this.auth.provider.servicing.location.facility;
            f.appendChild(this.createInput(w, 'facilityID', identifier?.nationalProvider || 'FAC-Unknown'));
            f.appendChild(this.createInput(w, 'facilityName', description || 'FAC-Unknown'));
            f.appendChild(this.createInput(w, 'facilityCity', address?.physical?.city || ''));
            f.appendChild(this.createInput(w, 'facilityState', address?.physical?.region?.code || ''));
            f.appendChild(this.createInput(w, isAutoAuth ? 'facilityZip' : 'facilityZipCode', address?.physical?.postalCode || ''));
            const npi = identifier?.nationalProvider && identifier.nationalProvider.length === 10 ? identifier.nationalProvider : '';
            f.appendChild(this.createInput(w, 'facilityNPI', npi));
            if(isAutoAuth) f.appendChild(this.createInput(w, 'facilityTaxID', identifier?.tax || ''));
        } else {
            f.appendChild(this.createInput(w, 'facilityID', 'FAC-Unknown'));
            f.appendChild(this.createInput(w, 'facilityNPI', ''));
        }
        if(this.auth.provider.requesting) {
            const { identifier, name, location } = this.auth.provider.requesting;
            const providerType = isAutoAuth ? 'requestingProvider' : 'admittingProvider';
            f.appendChild(this.createInput(w, providerType + 'ID', identifier?.primary || 'PRO-Unknown'));
            f.appendChild(this.createInput(w, providerType + 'FirstName', name?.first || identifier?.primary || 'NO_FIRST_NAME'));
            f.appendChild(this.createInput(w, providerType + 'LastName', name?.last || ''));
            const { city, region, postalCode } = location?.facility?.address?.physical || { city: null, region: null, postalCode: null };
            f.appendChild(this.createInput(w, providerType + 'City', city || ''));
            f.appendChild(this.createInput(w, providerType + 'State', region?.code || ''));
            f.appendChild(this.createInput(w, providerType + 'Zip', postalCode || ''));
            const npi = location?.facility?.identifier?.nationalProvider && location.facility.identifier.nationalProvider.length === 10 ? location.facility.identifier.nationalProvider : '';
            f.appendChild(this.createInput(w, providerType + 'NPI', npi));
            if(isAutoAuth) f.appendChild(this.createInput(w, 'requestingProviderTaxID', location?.facility?.identifier?.tax || ''));
        }
        if(this.auth.member.primaryCarePhysician) {  //TODO: Make sure this is included in the context, or fetch with a separate API call
            const { identifier, name } = this.auth.member.primaryCarePhysician;
            f.appendChild(this.createInput(w, 'pcpID', identifier?.primary || ''));
            f.appendChild(this.createInput(w, 'pcpFirstName', name?.first || identifier?.primary || 'NO_FIRST_NAME'));
            f.appendChild(this.createInput(w, 'pcpLastName', name?.last || ''));

            if(identifier?.nationalProvider) {
                f.appendChild(this.createInput(w, 'pcpNPI', identifier.nationalProvider));
            }
        }
    }

    private getVersionInfo(): string {
        const appVersion = version?.version || '0';
        return appVersion
    }

    private createInput(w: Window, name: string, value: string): HTMLInputElement {
        const i: HTMLInputElement = w.document.createElement('input');
        i.name = name;
        i.setAttribute('type', 'hidden');
        i.setAttribute('value', value);
        return i;
    }

    private getPatientID(member: CD.Member | null | undefined): string {
        const property = this.patientIDProperty || 'id';
        return resolveProperty(member, property) + '' || member?.id || '';
    }

    public getEpisodeID(authNumber: string): string {
        return this.instancePrefix + this.episodeIDPrefix + authNumber;
    }

    //Format ISO Date to a Date string based on string of tokens
    //  formatTokens reference: https://github.com/date-fns/date-fns/blob/master/docs/unicodeTokens.md
    private formatDate(isoDate: string, formatTokens: string): string {
        if(!isoDate) { return ''; }

        const baseDate = parseISO(isoDate);
        return format(baseDate, formatTokens);
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    public async updateExternalSystemID(): Promise<void> {
        if(this.auth?.externalSystem?.code != 'CWQI') {
            try {
                const updatedAuth: UM.Auth = {
                    id: this.auth!.id,
                    externalSystem: {
                        code: 'CWQI'
                    }
                };
                const response = await this.authService.saveAuth(updatedAuth);
                if(response.isSuccessful) {
                    if(this.auth) { this.auth.externalSystem = { code: 'CWQI' }; }
                }
            }
            catch(error) {
                throw error;
            }
            finally {
            }
        }

    }

    @apiCall
    public async verifyExternalSystemResult(episodeId: string): Promise<void> {
        if(!this.auth?.id) { throw new Error('Missing Auth ID'); }
        try {
            const authExtResponse: UM.Auth = {
                id: this.auth?.id,
                externalSystem: {
                    code: 'CWQI',
                    externalResultsIdentifier: episodeId
                }
            };
            const response = await this.authService.verifyExternalSystemResult(authExtResponse);
            if(response.isSuccessful && response.result) {
                this.events.publish(new AuthExternalSystemChangeEvent());
            } else {
                this.events.publish(new AutoAuthCanceledEvent());
            }
        }
        catch(error) {
            throw error;
        }
        finally {
        }
    }

    @apiCall
    private async getHashToken(description: string): Promise<ApiPostResponse> {
        const action = new ApiAction('singlesignon', 'account', { code: 'CWQI', description: description });
        return await this.api.post(action);
    }

    @apiCall
    private async getSummaryXml(description: string): Promise<ApiPostResponse> {
        const action = new ApiAction('singlesignon', 'account', { code: 'CWQI_Summary', description: description });
        return await this.api.post(action);
    }

    @apiCall
    private async getAutoAuthHashToken(description: string): Promise<ApiPostResponse> {
        const action = new ApiAction('singlesignon', 'account', { code: 'CWQI_AA', description: description });
        return await this.api.post(action);
    }

    @apiCall
    public async loadSystemSettings(): Promise<IApiSettings> {
        try {
            const response = await this.api.get(this.api.settings(`mcg/systems/`));
            return response.settings;
        }
        catch(error) {
            throw error;
        }
        finally {
        }
    }

}

export class CwqiChangedEvent { }
export class CwqiSavedEvent { }
export class CwqiSavedGuidelinesEvent { }
export class CwqiReloadedGuidelinesEvent { }
export class AutoAuthClosedEvent { }
export class AutoAuthCanceledEvent { }

// Combined Diagnosis and Service Codes
export interface ICodes {
    modelCode: string;  //CD.Diagnosis or CD.Service
    isPreCert: boolean;
    isPrimary: boolean;
    code: string;
    description: string;
    type: string;  //External System Mapping
    qty?: number;  //Only applicable to CD.Service
    determination?: string;
}
